import { logError } from '@/libs/logger/console-log';

// Per-endpoint, in-process throttle.
//
// When an upstream (e.g. api.roomster.com) goes down, the SSR path can throw on
// almost every render. Logging each one would flood stdout and steal event-loop
// time from the very requests we are trying to serve — making an incident worse.
// Instead we emit at most one detailed line per endpoint per window and report
// how many were suppressed, so the rate is still visible in Loki.
const LOG_THROTTLE_MS = 10_000;

const lastLoggedAt = new Map<string, number>();
const suppressedSinceLastLog = new Map<string, number>();

// Collapse id-like path segments and drop the query string so the throttle key
// (and any Loki query built on it) stays low-cardinality. Version segments such
// as "v1" are preserved; only segments starting with a digit or containing a
// comma (id lists) are masked.
function normalizeEndpoint(rawUrl: string): string {
  let path = rawUrl;

  try {
    path = new URL(rawUrl).pathname;
  } catch {
    const queryIndex = rawUrl.indexOf('?');
    path = queryIndex === -1 ? rawUrl : rawUrl.slice(0, queryIndex);
  }

  return path
    .split('/')
    .map((segment) => (/^\d/.test(segment) || segment.includes(',') ? ':id' : segment))
    .join('/');
}

type UpstreamErrorKind = 'aborted' | 'network' | 'timeout';

function classifyError(error: unknown): UpstreamErrorKind {
  const name = error && typeof error === 'object' && 'name' in error
    ? (error as { name?: string }).name
    : undefined;

  if (name === 'TimeoutError') {
    return 'timeout';
  }

  if (name === 'AbortError') {
    return 'aborted';
  }

  return 'network';
}

interface UpstreamErrorContext {
  functionName: string,
  url: string,
  extra?: Record<string, unknown>,
}

/**
 * Logs a thrown fetch error (timeout / network failure) to stdout, which
 * Promtail ships to Loki. Intentional cancellations ('aborted', e.g. a
 * superseded request) are ignored. This never re-throws — the caller keeps full
 * control of error propagation, so behaviour is unchanged; it only adds a signal.
 *
 * Query it in Loki with: |= "UPSTREAM_REQUEST_FAILED"
 */
export default function logUpstreamError(error: unknown, context: UpstreamErrorContext): void {
  const kind = classifyError(error);

  if (kind === 'aborted') {
    return;
  }

  const endpoint = normalizeEndpoint(context.url);
  const now = Date.now();
  const last = lastLoggedAt.get(endpoint) ?? 0;

  if (now - last < LOG_THROTTLE_MS) {
    suppressedSinceLastLog.set(endpoint, (suppressedSinceLastLog.get(endpoint) ?? 0) + 1);

    return;
  }

  const suppressed = suppressedSinceLastLog.get(endpoint) ?? 0;

  lastLoggedAt.set(endpoint, now);
  suppressedSinceLastLog.set(endpoint, 0);

  logError('UPSTREAM_REQUEST_FAILED', {
    endpoint,
    functionName: context.functionName,
    kind,
    message: error instanceof Error ? error.message : String(error),
    ...(suppressed > 0 && { suppressedSinceLastLog: suppressed }),
    ...(error instanceof Error && error.cause
      ? { causeCode: (error.cause as { code?: string }).code }
      : {}),
    ...context.extra,
  });
}
